import { Button } from '@cargoflow/ui/components/button'
import { Input } from '@cargoflow/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cargoflow/ui/components/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargoflow/ui/components/table'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useMemo } from 'react'
import { z } from 'zod'
import { StatusBadge } from '@/features/companies/status-badge'
import { api } from '@/lib/api'
import { formatRelative } from '@/lib/format'
import { queryKeys } from '@/lib/query-keys'

const ZodStatus = z.enum(['pending', 'approved', 'rejected'])
const ZodSort = z.enum(['created_at', 'updated_at', 'name', 'verification_status'])
const ZodOrder = z.enum(['asc', 'desc'])

const ZodSearch = z.object({
  page: z.coerce.number().min(1).default(1),
  status: ZodStatus.optional(),
  q: z.string().optional(),
  sort: ZodSort.optional(),
  order: ZodOrder.optional(),
})

const SORT_OPTIONS = [
  { value: 'created_at:desc', label: 'Newest first', sort: 'created_at', order: 'desc' },
  { value: 'created_at:asc', label: 'Oldest first', sort: 'created_at', order: 'asc' },
  { value: 'updated_at:desc', label: 'Recently updated', sort: 'updated_at', order: 'desc' },
  { value: 'name:asc', label: 'Name A→Z', sort: 'name', order: 'asc' },
  { value: 'name:desc', label: 'Name Z→A', sort: 'name', order: 'desc' },
  { value: 'verification_status:asc', label: 'Status', sort: 'verification_status', order: 'asc' },
] as const

export const Route = createFileRoute('/_authenticated/companies/')({
  validateSearch: ZodSearch,
  component: CompaniesList,
})

const PAGE_SIZE = 20

function CompaniesList() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const filters = useMemo(
    () => ({
      page: search.page,
      limit: PAGE_SIZE,
      status: search.status,
      search: search.q,
      sort: search.sort ?? 'created_at',
      order: search.order ?? 'desc',
    }),
    [search.page, search.status, search.q, search.sort, search.order],
  )

  const sortValue = `${filters.sort}:${filters.order}`

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: queryKeys.companies.list(filters),
    queryFn: async () => {
      const { data, error } = await api.admin.companies.get({ query: filters })
      if (error) throw new Error(typeof error.value === 'string' ? error.value : 'Failed to load')
      return data
    },
    placeholderData: keepPreviousData,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Companies</h1>
          <p className="text-muted-foreground text-sm">Review and verify carrier companies</p>
        </div>
      </div>

      <div className="flex gap-3">
        <Input
          placeholder="Search name, CUI, or email…"
          className="max-w-sm"
          defaultValue={search.q ?? ''}
          onBlur={(e) => {
            const value = e.target.value.trim()
            navigate({
              search: (prev) => ({ ...prev, q: value || undefined, page: 1 }),
            })
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const value = (e.target as HTMLInputElement).value.trim()
              navigate({
                search: (prev) => ({ ...prev, q: value || undefined, page: 1 }),
              })
            }
          }}
        />
        <Select
          value={search.status ?? 'all'}
          onValueChange={(value) => {
            navigate({
              search: (prev) => ({
                ...prev,
                status:
                  value === 'all' ? undefined : (value as 'pending' | 'approved' | 'rejected'),
                page: 1,
              }),
            })
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={sortValue}
          onValueChange={(value) => {
            const option = SORT_OPTIONS.find((opt) => opt.value === value)
            if (!option) return
            navigate({
              search: (prev) => ({
                ...prev,
                sort: option.sort,
                order: option.order,
                page: 1,
              }),
            })
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>CUI</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Docs</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  <Loader2 className="inline h-4 w-4 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell colSpan={8} className="text-destructive h-24 text-center">
                  {(error as Error).message}
                </TableCell>
              </TableRow>
            )}
            {data?.data.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground h-24 text-center">
                  No companies found
                </TableCell>
              </TableRow>
            )}
            {data?.data.map((company) => (
              <TableRow key={company.id}>
                <TableCell className="font-medium">{company.name}</TableCell>
                <TableCell className="font-mono text-xs">{company.cui}</TableCell>
                <TableCell className="text-sm">{company.contact_email}</TableCell>
                <TableCell className="text-sm">{company.address_country ?? '—'}</TableCell>
                <TableCell>{company.document_count}</TableCell>
                <TableCell>
                  <StatusBadge status={company.verification_status} />
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatRelative(company.updated_at)}
                </TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/companies/$company_id" params={{ company_id: company.id }}>
                      Review
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Page {data.page} of {data.total_pages} · {data.total} total
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.page <= 1 || isFetching}
              onClick={() => navigate({ search: (prev) => ({ ...prev, page: data.page - 1 }) })}
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.page >= data.total_pages || isFetching}
              onClick={() => navigate({ search: (prev) => ({ ...prev, page: data.page + 1 }) })}
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
